import { useMemo, useState } from "react";
import { useExportCampaignProducts } from "../../features/capmpaign/useCampaigns";
import { exportToExcel } from "../../utils/exportToExcel"
import {buildCampaignProductsExcelHeaders} from "../../utils/campaigns/campaignsExcel"

import {
  useAddCampaignProduct,
  useImportCampaignProducts,
  useProductSearch,
  useRemoveCampaignProduct,
} from "../../features/capmpaign/useCampaigns";

const getProductId = (product) =>
  product?.product_id ?? product?.id ?? null;

const CampaignProducts = ({ slug, products = [] }) => {
  const [query, setQuery] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");

  const { data: searchResults = [], isFetching } = useProductSearch(query);

  const addMutation = useAddCampaignProduct();
  const removeMutation = useRemoveCampaignProduct();
  const importMutation = useImportCampaignProducts();
  const { mutate: exportProducts, isPending: isExporting } =
    useExportCampaignProducts();


  const productIdsInCampaign = useMemo(() => {
    return new Set(products.map(getProductId).filter(Boolean));
  }, [products]);

  const filteredSearchResults = useMemo(() => {
    return (searchResults || []).filter((item) => {
      const id = getProductId(item);
      return id && !productIdsInCampaign.has(id);
    });
  }, [searchResults, productIdsInCampaign]);

  const handleAdd = async (product) => {
    const productId = getProductId(product);
    if (!productId) return;

    try {
      setError("");
      await addMutation.mutateAsync({ slug, productId });
      setQuery("");
    } catch (e) {
      setError(e?.response?.data?.detail || "افزودن کالا ناموفق بود.");
    }
  };

  const handleRemove = async (product) => {
    const productId = getProductId(product);
    if (!productId) return;

    try {
      setError("");
      await removeMutation.mutateAsync({ slug, productId });
    } catch (e) {
      setError(e?.response?.data?.detail || "حذف کالا ناموفق بود.");
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    try {
      setError("");
      await importMutation.mutateAsync({ slug, file: selectedFile });
      setSelectedFile(null);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "درون‌ریزی اکسل ناموفق بود."
      );
    }
  };

  const handleExportProducts = () => {
    exportProducts(slug, {
      onSuccess: (data) => {
        const items = data?.items || [];

        const excelData = items.map((item) => ({
          product_id: item.product_id ?? "",
          product_name: item.product_name ?? "",
          product_code: item.product_code ?? "",
          product_brand: item.product_brand ?? "",
          product_category: item.product_category ?? "",
          supplier_name: item.supplier_name ?? "",
          created_at: item.created_at ?? "",
        }));

        exportToExcel(
          excelData,
          `campaign-products-${data.campaign_slug}`,
          { headers: buildCampaignProductsExcelHeaders() }
        );
      },
    });
  };

  return (
    <section dir="rtl" className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-4 sm:px-5">
        <h2 className="text-sm font-semibold text-gray-800 sm:text-base">
          تخصیص کالا به رویداد
        </h2>
      </div>

      <div className="p-4 sm:p-5 space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              جستجوی کالا
            </label>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="نام کالا، SKU یا دسته‌بندی..."
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-[#1E3A5F]"
            />

            <div className="max-h-80 overflow-auto rounded-lg border border-gray-200">
              {isFetching ? (
                <div className="p-4 text-sm text-gray-500">در حال جستجو...</div>
              ) : filteredSearchResults.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">نتیجه‌ای یافت نشد.</div>
              ) : (
                filteredSearchResults.map((product) => {
                const productId = getProductId(product);

                const productName = product.name || "بدون عنوان";

                const imageUrl = product.featured_image_url || "/placeholder.png";

                const category = product.category_name || "-";

                const brand = product.brand || "-";

                const supplier = product.supplier_name || "-";

                const productCode =product.product_code || "-";

                return (
                  <div
                    key={productId}
                    className="flex items-center justify-between gap-3 border-b border-gray-100 p-3 last:border-b-0"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <img
                        src={imageUrl}
                        alt={productName}
                        className="h-14 w-14 shrink-0 rounded-lg border border-gray-200 object-cover"
                        onError={(event) => {
                          event.currentTarget.src = "/placeholder.png";
                        }}
                      />

                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900">
                          {productName}
                        </div>

                        <div className="mt-1 text-xs text-gray-500">
                          کد کالا: {productCode}
                        </div>

                        <div className="mt-1 truncate text-xs text-gray-500">
                          دسته: {category} | برند: {brand}
                        </div>

                        <div className="mt-1 truncate text-xs text-gray-500">
                          تامین‌کننده: {supplier}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleAdd(product)}
                      disabled={addMutation.isPending}
                      className="shrink-0 rounded-lg bg-[#1E3A5F] px-3 py-2 text-xs font-medium text-white hover:bg-[#2B5388] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      افزودن
                    </button>
                  </div>
                );
              })

              )}
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              بارگذاری اکسل کالاها
            </label>

            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="block w-full rounded-lg border border-gray-300 p-2 text-sm"
              />
              <button
                  type="button"
                  onClick={handleImport}
                  disabled={!selectedFile || importMutation.isPending}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {importMutation.isPending ? "در حال آپلود..." : "آپلود"}
              </button>
            </div>

            <button
                type="button"
                onClick={handleExportProducts}
                disabled={isExporting}
                className="inline-block text-sm text-[#1E3A5F] underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? "در حال دریافت..." : "دانلود خروجی اکسل کالاها"}
            </button>
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-sm font-semibold text-gray-800">
            کالاهای متصل
          </h3>

          <div>
            {products.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  هنوز کالایی به این کمپین متصل نشده است.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                  {products.map((product) => {
                    const productId = getProductId(product);
                    return (
                        <div
                            key={productId}
                            className="grid grid-cols-[64px_1fr_auto] items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
                        >
                          <img
                              src={product?.featured_image_url || product?.thumbnail || "/placeholder.png"}
                              alt={product?.product_name || "product"}
                              className="h-16 w-16 flex-shrink-0 rounded-lg border border-gray-200 object-cover"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {product?.product_name || "بدون عنوان"}
                            </div>
                            <div className="mt-1 truncate text-xs text-gray-500">
                              {product?.product_category || "-"} | {product?.product_brand || "-"} | {product?.supplier_name || "-"}
                            </div>
                          </div>
                          <button
                              type="button"
                              onClick={() => handleRemove(product)}
                              className="whitespace-nowrap rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                          >
                            حذف
                          </button>
                        </div>
                    );
                  })}
                </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CampaignProducts;
